/**
 * React Query hooks + view helpers for Travel. A trip is a container:
 * dates, notes, and a packing checklist on the trip itself; tasks and
 * documents link in via their own `tripId` and stay owned by their own
 * tools. Optimistic updates mirror the budget/documents patterns —
 * the cache is the source of truth offline.
 */

import type {
  DocumentDTO,
  ObligationDTO,
  PackingItem,
  TripCreateInput,
  TripDTO,
  TripUpdateInput,
} from '@almanac/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { mutationKeys, queryKeys } from './queryClient.js';

export function useTrips() {
  return useQuery<TripDTO[]>({
    queryKey: queryKeys.trips,
    queryFn: api.listTrips,
  });
}

type TripListUpdater = (previous: TripDTO[] | undefined) => TripDTO[] | undefined;

function patchTrips(qc: ReturnType<typeof useQueryClient>, updater: TripListUpdater) {
  qc.setQueryData<TripDTO[]>(queryKeys.trips, (prev) => updater(prev));
}

export function useCreateTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.createTrip,
    mutationFn: (input: TripCreateInput & { id: string }) => api.createTrip(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: queryKeys.trips });
      const previous = qc.getQueryData<TripDTO[]>(queryKeys.trips);
      const now = new Date().toISOString();
      const optimistic: TripDTO = {
        id: input.id,
        title: input.title,
        destination: input.destination ?? null,
        startDate: input.startDate,
        endDate: input.endDate ?? null,
        notes: input.notes ?? null,
        packing: input.packing ?? [],
        visibility: input.visibility ?? 'private',
        createdBy: 'me',
        createdAt: now,
        updatedAt: now,
      };
      patchTrips(qc, (prev) => [...(prev ?? []), optimistic]);
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.trips, ctx.previous);
    },
    onSuccess: (server) => {
      patchTrips(qc, (prev) => (prev ?? []).map((t) => (t.id === server.id ? server : t)));
    },
  });
}

export function useUpdateTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.updateTrip,
    mutationFn: (vars: { id: string; input: TripUpdateInput }) =>
      api.updateTrip(vars.id, vars.input),
    onMutate: async ({ id, input }) => {
      await qc.cancelQueries({ queryKey: queryKeys.trips });
      const previous = qc.getQueryData<TripDTO[]>(queryKeys.trips);
      patchTrips(qc, (prev) =>
        (prev ?? []).map((t) =>
          t.id === id
            ? {
                ...t,
                ...input,
                destination:
                  input.destination === undefined ? t.destination : (input.destination ?? null),
                endDate: input.endDate === undefined ? t.endDate : (input.endDate ?? null),
                notes: input.notes === undefined ? t.notes : (input.notes ?? null),
                packing: input.packing === undefined ? t.packing : input.packing,
                updatedAt: new Date().toISOString(),
              }
            : t,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.trips, ctx.previous);
    },
    onSuccess: (server) => {
      patchTrips(qc, (prev) => (prev ?? []).map((t) => (t.id === server.id ? server : t)));
    },
  });
}

export function useDeleteTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.deleteTrip,
    mutationFn: (vars: { id: string }) => api.deleteTrip(vars.id),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: queryKeys.trips });
      const previous = qc.getQueryData<TripDTO[]>(queryKeys.trips);
      patchTrips(qc, (prev) => (prev ?? []).filter((t) => t.id !== id));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.trips, ctx.previous);
    },
  });
}

// ── View helpers (pure) ─────────────────────────────────────────────

function isoDay(d: Date): string {
  const p = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export type TripPhase = 'upcoming' | 'current' | 'past';

/** Where the trip sits relative to today. A missing end date treats
 *  the start date as the whole trip. */
export function tripPhase(trip: TripDTO, now: Date): TripPhase {
  const today = isoDay(now);
  const end = trip.endDate ?? trip.startDate;
  if (end < today) return 'past';
  if (trip.startDate <= today) return 'current';
  return 'upcoming';
}

export interface TripShelf {
  /** The hero: the trip underway, or the soonest upcoming one. */
  next: TripDTO | null;
  /** Upcoming trips beyond the hero, soonest first. */
  upcoming: TripDTO[];
  /** Finished trips, most recent first. */
  past: TripDTO[];
}

export function splitTrips(trips: TripDTO[], now: Date): TripShelf {
  const active = trips
    .filter((t) => tripPhase(t, now) !== 'past')
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const past = trips
    .filter((t) => tripPhase(t, now) === 'past')
    .sort((a, b) => b.startDate.localeCompare(a.startDate));
  // A trip underway outranks any upcoming one, whatever the dates say.
  const current = active.find((t) => tripPhase(t, now) === 'current') ?? null;
  const next = current ?? active[0] ?? null;
  return { next, upcoming: active.filter((t) => t.id !== next?.id), past };
}

/** '18 Dec 2026 – 10 Jan 2027 · 24 days' (single-day trips: one date). */
export function tripDatesLabel(trip: TripDTO): string {
  const fmt = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  if (!trip.endDate || trip.endDate === trip.startDate) return fmt(trip.startDate);
  const days =
    Math.round(
      (new Date(`${trip.endDate}T00:00:00`).getTime() -
        new Date(`${trip.startDate}T00:00:00`).getTime()) /
        (1000 * 60 * 60 * 24),
    ) + 1;
  return `${fmt(trip.startDate)} – ${fmt(trip.endDate)} · ${days} days`;
}

/** Compact distance to the trip: 'in 5mo', 'in 3d', 'now', 'Jul 2025'. */
export function tripWhenLabel(trip: TripDTO, now: Date): string {
  const phase = tripPhase(trip, now);
  if (phase === 'current') return 'now';
  if (phase === 'past') {
    return new Date(`${trip.startDate}T00:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric',
    });
  }
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const diff = Math.round(
    (new Date(`${trip.startDate}T00:00:00`).getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff < 7) return `in ${diff}d`;
  if (diff < 30) return `in ${Math.round(diff / 7)}w`;
  if (diff < 365) return `in ${Math.round(diff / 30)}mo`;
  return `in ${Math.round(diff / 365)}y`;
}

/** Active tasks linked to the trip, due-date order. */
export function tripTasks(obligations: ObligationDTO[], tripId: string): ObligationDTO[] {
  return obligations
    .filter((o) => o.tripId === tripId && o.status !== 'completed')
    .sort((a, b) =>
      ((a.nextDueDate ?? a.dueDate) || '').localeCompare((b.nextDueDate ?? b.dueDate) || ''),
    );
}

export function tripCompletedTaskCount(obligations: ObligationDTO[], tripId: string): number {
  return obligations.filter((o) => o.tripId === tripId && o.status === 'completed').length;
}

/** Documents linked to the trip, newest first. */
export function tripDocuments(documents: DocumentDTO[], tripId: string): DocumentDTO[] {
  return documents
    .filter((d) => d.tripId === tripId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export interface TripReadiness {
  done: number;
  total: number;
}

/** The readiness bar: completed tasks + packed items over everything
 *  the trip is waiting on. Null when there's nothing to count yet. */
export function tripReadiness(
  trip: TripDTO,
  obligations: ObligationDTO[],
): TripReadiness | null {
  const open = tripTasks(obligations, trip.id).length;
  const doneTasks = tripCompletedTaskCount(obligations, trip.id);
  const packed = trip.packing.filter((p) => p.done).length;
  const total = open + doneTasks + trip.packing.length;
  if (total === 0) return null;
  return { done: doneTasks + packed, total };
}

// ── Packing edits (pure, whole-list) ────────────────────────────────

export function addPackingItem(list: PackingItem[], label: string, qty?: string): PackingItem[] {
  const trimmed = label.trim();
  if (!trimmed) return list;
  return [
    ...list,
    { id: crypto.randomUUID(), label: trimmed, qty: qty?.trim() || null, done: false },
  ];
}

export function togglePackingItem(list: PackingItem[], id: string): PackingItem[] {
  return list.map((p) => (p.id === id ? { ...p, done: !p.done } : p));
}

export function removePackingItem(list: PackingItem[], id: string): PackingItem[] {
  return list.filter((p) => p.id !== id);
}

/** Merge suggested labels into the list, skipping ones already there
 *  (case-insensitive) — used by both the AI suggestions and the
 *  carry-forward template. */
export function mergePackingLabels(list: PackingItem[], labels: string[]): PackingItem[] {
  const seen = new Set(list.map((p) => p.label.trim().toLowerCase()));
  let next = list;
  for (const raw of labels) {
    const label = raw.trim();
    if (!label || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    next = addPackingItem(next, label);
  }
  return next;
}

/** The reusable template, the quiet way: the most recently started
 *  trip's packing labels, unchecked. Refine a list once and every
 *  later trip starts from it. */
export function packingTemplateFrom(trips: TripDTO[], excludeId?: string): string[] {
  const source = [...trips]
    .filter((t) => t.id !== excludeId && t.packing.length > 0)
    .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
  return source ? source.packing.map((p) => p.label) : [];
}

// ── Starter tasks (the Tripsy borrowing) ────────────────────────────

export interface StarterTaskDraft {
  key: string;
  title: string;
  dueDate: string; // YYYY-MM-DD, clamped to today
}

/** Suggested pre-trip tasks offered as chips when a trip is created:
 *  standard errands timed back from departure, clamped to today when
 *  the trip is close. The user picks which become real obligations. */
export function starterTaskDrafts(
  tripTitle: string,
  startDate: string,
  now: Date,
): StarterTaskDraft[] {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const daysBefore = (n: number) => {
    const d = new Date(`${startDate}T00:00:00`);
    d.setDate(d.getDate() - n);
    return isoDay(d < today ? today : d);
  };
  return [
    { key: 'passports', title: `Check passports & visas for ${tripTitle}`, dueDate: daysBefore(90) },
    { key: 'insurance', title: `Book travel insurance for ${tripTitle}`, dueDate: daysBefore(30) },
    { key: 'checkin', title: `Online check-in — ${tripTitle}`, dueDate: daysBefore(1) },
  ];
}

/** Status line for the Travel tile on Today. */
export function travelTileSummary(
  trips: TripDTO[] | undefined,
  now: Date,
): string | undefined {
  if (!trips) return undefined;
  const next = splitTrips(trips, now).next;
  if (!next) return 'Plan a trip';
  return tripPhase(next, now) === 'current'
    ? `${next.title} · now`
    : `${next.title} · ${tripWhenLabel(next, now)}`;
}

// ── Documents-on-a-trip helpers (link sheet + wallet) ───────────────

/** Kinds that read as travel paperwork — used to float likely
 *  candidates in the link sheet and to bucket the wallet. Free-text
 *  kinds, so regexes over the vocabulary rather than an enum. */
const TICKETS_RE = /ticket|boarding|booking|reservation|itinerar|voucher|rental|hotel|flight|transport|rail|ferry/i;
const INSURANCE_RE = /insurance|policy|assistance/i;
const IDENTITY_RE = /passport|visa|permit|identity|id\s*card|residence|licen[cs]e/i;

function docNewestFirst(a: DocumentDTO, b: DocumentDTO): number {
  return (b.issuedDate ?? b.createdAt).localeCompare(a.issuedDate ?? a.createdAt);
}

export interface LinkSheetGroups {
  linked: DocumentDTO[];
  likely: DocumentDTO[];
  rest: DocumentDTO[];
}

/** The link sheet's shelves: already-linked pinned first, then
 *  documents that look like they belong to this trip (travel-shaped
 *  kind, or issued near the trip window), then everything else —
 *  each newest first, all filtered by the search query over
 *  title / kind / issuer. */
export function linkSheetGroups(
  docs: DocumentDTO[],
  trip: TripDTO,
  query: string,
): LinkSheetGroups {
  const q = query.trim().toLowerCase();
  const matches = (d: DocumentDTO) =>
    !q ||
    [d.title, d.category ?? '', d.issuer ?? ''].some((s) => s.toLowerCase().includes(q));
  const windowStart = new Date(`${trip.startDate}T00:00:00`);
  windowStart.setDate(windowStart.getDate() - 90);
  const startIso = isoDay(windowStart);
  const endIso = trip.endDate ?? trip.startDate;
  const travelShaped = (d: DocumentDTO) =>
    TICKETS_RE.test(d.category ?? '') ||
    INSURANCE_RE.test(d.category ?? '') ||
    IDENTITY_RE.test(d.category ?? '') ||
    Boolean(d.issuedDate && d.issuedDate >= startIso && d.issuedDate <= endIso);
  const pool = docs.filter(matches);
  const linked = pool.filter((d) => d.tripId === trip.id).sort(docNewestFirst);
  const unlinked = pool.filter((d) => d.tripId !== trip.id);
  return {
    linked,
    likely: unlinked.filter(travelShaped).sort(docNewestFirst),
    rest: unlinked.filter((d) => !travelShaped(d)).sort(docNewestFirst),
  };
}

export interface WalletGroup {
  key: 'tickets' | 'insurance' | 'other';
  label: string;
  rows: DocumentDTO[];
}

/** The trip section's kind buckets. Empty buckets drop out; a section
 *  with only "other" renders flat (the caller skips the subheading). */
export function walletGroups(linkedDocs: DocumentDTO[]): WalletGroup[] {
  const groups: WalletGroup[] = [
    { key: 'tickets', label: 'Tickets & bookings', rows: [] },
    { key: 'insurance', label: 'Insurance', rows: [] },
    { key: 'other', label: 'Other', rows: [] },
  ];
  for (const d of [...linkedDocs].sort(docNewestFirst)) {
    if (TICKETS_RE.test(d.category ?? '')) groups[0]!.rows.push(d);
    else if (INSURANCE_RE.test(d.category ?? '')) groups[1]!.rows.push(d);
    else groups[2]!.rows.push(d);
  }
  return groups.filter((g) => g.rows.length > 0);
}

/** Vault identity records (passports, visas, permits) — surfaced
 *  read-only on an active trip's page: they're the documents a border
 *  actually asks for, and they're already kept offline by the Vault. */
export function vaultIdentityDocs(docs: DocumentDTO[]): DocumentDTO[] {
  return docs
    .filter((d) => d.documentCategory === 'vital' && IDENTITY_RE.test(d.category ?? ''))
    .sort((a, b) => a.title.localeCompare(b.title));
}
